import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { bool, func } from 'prop-types'
import { Button, Modal, Progress, Upload } from 'antd'
import { useSelector } from 'react-redux'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../reducers/UserReducer'
import { onError, onSuccess, onWarning } from '../../utils/apiHelper'
import { ImportModes, SPREADSHEET_UPLOAD_TYPES } from '../../utils/constants'
import { importStudents } from '../../utils/api/student'
import useFakeProgress from '../../hooks/useFakeProgress'
import useScreenSize from '../../hooks/useScreen'
import { isFileTooLarge } from '../../utils/files'
import ImportReport, { translateCode as translateImportCode } from '../shared/ImportReport/ImportReport'
import FileTooLargeMessage from '../shared/Messages/FileTooLargeMessage'

import '../../assets/students-import-modal.scss'

const { Dragger } = Upload

const TRANSLATIONS = 'students_import'

const ACCEPTED_TYPES = Object.freeze([...SPREADSHEET_UPLOAD_TYPES, '.csv', 'text/csv'])
const ACCEPTED_EXTENSIONS = /\.(xlsx|xls|csv)$/i

// Some systems send a csv without a mime type.
const isAccepted = file => ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.test(file.name)

const UPLOAD_WIDTH = 860
const REPORT_WIDTH = 1100
const DETAILED_REPORT_WIDTH = 1400
const PROGRESS_INTERVAL = 400

const Steps = Object.freeze({
  UPLOAD: 'UPLOAD',
  PREVIEW: 'PREVIEW',
  DONE: 'DONE'
})

const translateCode = (code, t) => translateImportCode(code, t, TRANSLATIONS)

const EMPTY_MARKER = '/'

const identifyStudent = ({ student }) => {
  const name = [student.lastname, student.firstname]
    .filter(part => part && part !== EMPTY_MARKER)
    .join(' ')

  return { title: name || student.email, detail: student.email }
}

const StudentsImportModal = ({ displayed, onClose, reloadStudentsAfterImport = () => {} }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const screen = useScreenSize()
  const { percentage, start: startProgress, complete: completeProgress, reset: resetProgress } = useFakeProgress(PROGRESS_INTERVAL)

  const [step, setStep] = useState(Steps.UPLOAD)
  const [fileList, setFileList] = useState([])
  const [tooLargeFile, setTooLargeFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [report, setReport] = useState(null)

  const unmounted = useRef(false)

  useEffect(() => () => { unmounted.current = true }, [])

  const acceptedTypes = useMemo(() => ACCEPTED_TYPES.join(','), [])

  const close = useCallback(() => {
    if (busy) {
      return
    }

    resetProgress()
    setStep(Steps.UPLOAD)
    setFileList([])
    setTooLargeFile(null)
    setReport(null)
    onClose()
  }, [busy, onClose, resetProgress])

  const announceImport = useCallback(summary => {
    const written = summary.created + summary.updated

    if (written > 0) {
      reloadStudentsAfterImport()
    }

    if (summary.errors === 0) {
      return onSuccess(t(`${TRANSLATIONS}.import.success`))
    }

    return written > 0
      ? onWarning(t(`${TRANSLATIONS}.import.partial`))
      : onError(t(`${TRANSLATIONS}.import.failure`))
  }, [reloadStudentsAfterImport, t])

  const send = useCallback(mode => {
    if (fileList.length === 0) {
      return
    }

    setBusy(true)
    startProgress()

    importStudents(user, fileList[0], mode).then(received => {
      if (unmounted.current) {
        return
      }

      completeProgress()
      setReport(received)
      setStep(mode === ImportModes.IMPORT ? Steps.DONE : Steps.PREVIEW)
      setBusy(false)

      if (mode === ImportModes.IMPORT) {
        announceImport(received.summary)
      }
    }, error => {
      if (unmounted.current) {
        return
      }

      resetProgress()
      setBusy(false)

      onError(error && error.message
        ? translateCode(error.message, t)
        : t(`${TRANSLATIONS}.errors.UNEXPECTED`)
      )
    })
  }, [announceImport, completeProgress, fileList, resetProgress, startProgress, t, user])

  const showingReport = step !== Steps.UPLOAD && report !== null

  const modalWidth = useMemo(() => {
    if (!showingReport) {
      return UPLOAD_WIDTH
    }

    const detailed = report.data.some(item => item.errors || item.warnings)

    return Math.min(detailed ? DETAILED_REPORT_WIDTH : REPORT_WIDTH, Math.round(screen.width * 0.9))
  }, [report, screen.width, showingReport])

  const writableRows = useMemo(
    () => report === null ? 0 : report.summary.created + report.summary.updated,
    [report]
  )

  const footer = useMemo(() => {
    if (step === Steps.DONE) {
      return [
        <Button key='close' type='primary' onClick={close}>{t('Close')}</Button>
      ]
    }

    if (step === Steps.PREVIEW) {
      return [
        <Button key='back' disabled={busy} onClick={() => setStep(Steps.UPLOAD)}>{t('Back')}</Button>,
        <Button
          key='confirm'
          type='primary'
          loading={busy}
          disabled={busy || writableRows === 0}
          onClick={() => send(ImportModes.IMPORT)}
        >
          {t(`${TRANSLATIONS}.confirm`)}
        </Button>
      ]
    }

    return [
      <Button key='cancel' disabled={busy} onClick={close}>{t('Cancel')}</Button>,
      <Button
        key='analyse'
        type='primary'
        loading={busy}
        disabled={busy || fileList.length === 0}
        onClick={() => send(ImportModes.DRY_RUN)}
      >
        {t(`${TRANSLATIONS}.analyse`)}
      </Button>
    ]
  }, [busy, close, fileList.length, send, step, t, writableRows])

  return (
    <Modal
      title={t('Import students')}
      visible={displayed}
      width={modalWidth}
      footer={footer}
      centered
      maskClosable={!busy}
      closable={!busy}
      onCancel={close}
      destroyOnClose
    >
      <div className='students-import-modal-body import-modal-body'>
        {busy && (
          <div className='importing-animation'>
            <Progress type='circle' percent={percentage} />
            <p className='importing-label'>
              {t(step === Steps.PREVIEW ? `${TRANSLATIONS}.importing` : `${TRANSLATIONS}.analysing`)}
            </p>
          </div>
        )}
        {!busy && step === Steps.UPLOAD && (
          <>
            <div className='upload-type-description'>
              <ul>
                <li>{t(`${TRANSLATIONS}.description.preview`)}</li>
                <li>{t(`${TRANSLATIONS}.description.update`)}</li>
                <li>{t(`${TRANSLATIONS}.description.blank_cells`)}</li>
                <li>{t(`${TRANSLATIONS}.description.delete`)}</li>
              </ul>
            </div>
            <div className='upload-area'>
              <Dragger
                fileList={fileList}
                name='file'
                accept={acceptedTypes}
                beforeUpload={file => {
                  if (!isAccepted(file)) {
                    onError(t(`${TRANSLATIONS}.errors.UNSUPPORTED_FILE_TYPE`))

                    return false
                  }

                  if (isFileTooLarge(file)) {
                    setTooLargeFile(file)
                    setFileList([])

                    return false
                  }

                  setTooLargeFile(null)
                  setFileList([file])

                  return false
                }}
                onRemove={() => setFileList([])}
              >
                <p className='ant-upload-text'>{t(`${TRANSLATIONS}.upload_text`)}</p>
                <p className='ant-upload-hint'>{t(`${TRANSLATIONS}.upload_hint`)}</p>
              </Dragger>
              <FileTooLargeMessage file={tooLargeFile} />
            </div>
          </>
        )}
        {!busy && showingReport && (
          <ImportReport
            entity='student'
            height={screen.height}
            identify={identifyStudent}
            prefix={TRANSLATIONS}
            report={report}
            t={t}
          />
        )}
      </div>
    </Modal>
  )
}

StudentsImportModal.propTypes = {
  displayed: bool.isRequired,
  onClose: func.isRequired,
  reloadStudentsAfterImport: func
}

export default StudentsImportModal
